export type Locale = 'en' | 'pt';

// language is "en" | "pt" from AppContext
export const languageToDateLocale = (language: string): string => {
  switch (language) {
    case 'pt':
      return 'pt-PT';
    case 'en':
    default:
      return 'en-US';
  }
};

export function getTranslation(locale: Locale, key: string, fallback: string): string {
  const lang = translations[locale];
  if (!lang) return fallback;

  // Access nested keys using dot notation
  const value = (lang as any)[key];
  return value || fallback;
}

export const translations: Record<Locale, Record<string, string>> = {
  en: {
    'header.crisis': 'Crisis resources',
    'header.support': 'Support',
    'i18n.lang': 'Language',
    'tabs.presence': 'Presence',
    'tabs.pinboard': 'Pinboard',
    'tabs.bookmarks': 'Bookmarks',

    // Home page
    'home.hello': 'Hello',
    'home.friend': 'friend',
    'home.howAreYouToday': 'How are you today?',
    'home.howAreYouFeeling': 'How are you feeling?',
    'home.breatheNow': 'Breathe now',
    'home.trackMood': 'Track mood',
    'home.trackNow': 'Track now',
    'home.lastRecorded': 'Last recorded',
    'home.feelingHopeful': 'Feeling hopeful',
    'home.feelingNeutral': 'Feeling neutral',
    'home.havingToughDay': 'Having a tough day',
    'home.updated': 'Updated',
    'time.justNow': 'Just now',
    'time.minutesAgo': 'minutes ago',
    'time.hoursAgo': 'hours ago',
    'time.daysAgo': 'days ago',
    'time.weeksAgo': 'weeks ago',
    'time.monthsAgo': 'months ago',
    'time.yearsAgo': 'years ago',
    'home.noMoodYet': 'No mood recorded yet',
    'home.recordFirstMood': 'Record your first mood',
    'home.yourDailyActs': 'Your daily acts',
    'home.customize': 'Customize',
    'home.complete': '{completed} of {total} complete',
    'home.todaysInspiration': "Today's inspiration",
    'home.dailyContent': 'Daily content',
    'home.share': 'Share',
    'home.needAMoment': 'Need a moment?',
    'home.instantRelief': 'Instant Relief',
    'home.feelingOverwhelmed':
      'Feeling overwhelmed? Take a quick pause with guided breathing.',
    'home.breatheWithMe': 'Breathe with me',
    
    // Panic mode / breathing exercise
    'panic.breatheWithMe': 'Breathe with me',
    'panic.focusOnBreathing': 'Focus on your breath',
    'panic.inhale': 'Inhale',
    'panic.exhale': 'Exhale',
    'panic.progress': 'PROGRESS',
    'panic.breaths': 'breaths',
    'panic.thisMoment': 'This moment is yours',
    'panic.safeAndSupported': 'You are safe and supported',
    'panic.feelBetterNow': 'I feel better now',

    // Community page
    'community.whosHere': "Who's Here?",
    'community.feelComfort': "Feel the comfort of others who understand what you're going through, no matter what time of day.",
    'community.expresGratitude': 'Express Gratitude',
    'community.gratitudeHelps': 'Finding moments of gratitude can help shift your perspective. Send a virtual "thank you" to someone who might need it today.',
    'community.readAndConnect': 'Read and connect with reflections from people near your location.',
    'community.nearbyReflections': 'Nearby Reflections',
    'community.globalReflections': 'Global Reflections',
    'community.anonymousDisclaimer': 'All content in the community is anonymous by default',
    'community.sendAnonymousGratitude': 'Send Anonymous Gratitude',
    'community.sendGratitude': 'Send Gratitude',
    'community.selectSomeoneNearby': 'Select Someone Nearby',
    'community.gratitudeBanner': "Every time you send gratitude, someone in your region will receive a notification that they've been appreciated.",
    'community.noNearbyUsers': 'No nearby users found. Your gratitude will be sent anonymously to someone in the global community.',
    'community.anonymousUser': 'Anonymous User',
    'community.anonymousUserAvatar': 'Anonymous user avatar',
    'community.lastActive': 'Last active',
    'community.selected': 'Selected',
    'community.gratitudeMessage': 'Add a custom message of gratitude (optional)',
    'community.gratitudeForLike': 'Someone appreciated your reflection. Your sharing matters and has touched someone today.',
    'community.defaultGratitudeMessage': 'Thank you for being part of this community. Your presence makes a difference.',
    'community.hide': 'Hide',
    'community.loadingNearbyUsers': 'Loading nearby users...',
    'community.sending': 'Sending...',
    'community.shareYourThoughts': 'Share Your Thoughts',
    'community.shareSpace': "This is a space to share reflections, ask questions, or simply express what's on your mind.",
    'community.placeholder': "What's on your mind today? Share your thoughts with the community...",
    'community.characters': 'characters',
    'community.moodPrompt': "Select one or more moods that describe how you're feeling",
    'community.postReflection': 'Post Reflection',
    'community.currentReflections': 'Current Reflections',
    'community.connectNearby': 'Connect with people in your area who are going through similar experiences.',
    'community.reflectionsNearYou': 'Reflections Near You',
    'community.savedReflections': 'Your Saved Reflections',
    'community.savedSubtitle': "Reflections you've saved for later. Bookmark posts that resonate with you to revisit them.",
    'community.bookmarkedReflections': 'Bookmarked Reflections',
    'community.loadingReflections': 'Loading reflections...',
    'community.loadingNearbyReflections': 'Loading nearby reflections...',
    'community.loadingBookmarks': 'Loading bookmarked reflections...',
    'community.noPostsYet': 'No reflections yet. Be the first to share!',
    'community.noNearbyYet': 'No nearby reflections yet.',
    'community.noNearbyLater': 'No nearby reflections found. Try checking back later.',
    'community.noBookmarks': "No bookmarked reflections yet. Start bookmarking posts you want to save!",
    'community.shareReflection': 'Share a reflection to be the first',
    'community.anonymous': 'Anonymous',
    'community.location': 'Location',
    'community.justNow': 'Just now',
    'community.like': 'Like',
    'community.bookmark': 'Bookmark',
    'community.report': 'Report',
    'community.saved': 'Saved',
    'community.likePost': 'Like post',
    'community.unlikePost': 'Unlike post',
    'community.addBookmark': 'Add bookmark',
    'community.removeBookmark': 'Remove bookmark',
    'community.reportPost': 'Report post',
    'community.reported': 'Reported',
    'community.reportThanks': 'Thanks for keeping the space supportive.',
    'community.reportFailed': 'Report failed',
    'community.reportError': 'Please try again later.',
    'community.prev': 'Prev',
    'community.gratitudeSent': 'Your gratitude has been sent!',
    'community.gratitudeFailed': 'Failed to send gratitude',
    'community.serverError': 'Server error. Please try again.',
    'community.networkError': 'Network error. Please try again.',
    'community.failedToLoadNearby': 'Could not load nearby users. Please try again.',

    // Mood/Track page moods
    'mood.peaceful': 'Peaceful',
    'mood.hopeful': 'Hopeful',
    'mood.grateful': 'Grateful',
    'mood.excited': 'Excited',
    'mood.motivated': 'Motivated',
    'mood.inspired': 'Inspired',
    'mood.joyful': 'Joyful',
    'mood.proud': 'Proud',
    'mood.content': 'Content',
    'mood.reflective': 'Reflective',
    'mood.curious': 'Curious',
    'mood.relaxed': 'Relaxed',
    'mood.anxious': 'Anxious',
    'mood.overwhelmed': 'Overwhelmed',
    'mood.frustrated': 'Frustrated',
    'mood.lonely': 'Lonely',
    'mood.melancholic': 'Melancholic',
    'mood.irritated': 'Irritated',
    'mood.calm': 'Calm',
    'mood.neutral': 'Neutral',
    'mood.stressed': 'Stressed',

    // Tasks page
    'tasks.journal.title.min': "Title must be at least 2 characters",
    'tasks.journal.title.max': "Title must not exceed 100 characters",
    'tasks.journal.content.min': "Content must be at least 5 characters",
    'tasks.journal.content.max': "Content must not exceed 1000 characters",
    'tasks.title': 'Track Your Journey',
    'tasks.subtitle': 'See your progress over time',
    'tasks.noData': 'No data yet. Start tracking to see your progress.',
    'tasks.emotionLevel': 'Emotion Level',
    'tasks.emotionLevels': 'Emotion Levels',
    'tasks.viewDetails': 'View Details',
    'tasks.fatigueLevel': 'Fatigue Level',
    'tasks.fatigueLevels': 'Fatigue Levels',
    'tasks.activityHistoryEmpty': 'No activity history yet',

    // Track page Activities
    'track.todayActivities.title': "Today's activities",
    'track.todayActivities.empty': 'No activities yet',
    'track.todayActivities.customize': 'Customize your activities',
    'track.todayActivities.viewAll': 'View all activities',

    // Track page Journal Entries
    'track.journalEntries.title': 'Journal Entries',
    'track.journalEntries.newEntry': 'New Entry',
    'track.journalEntries.empty': 'No journal entries yet',
    'track.journalEntries.journal': 'Journal',
    'track.journalEntries.dialogTitle': 'Share journal entry',
    'track.journalEntries.viewAll': 'View all entries',
    'track.journalEntries.description': 'Your personal reflections and notes',
    'track.journalEntries.copiedAlert': 'Journal entry copied to clipboard',
    'track.journalEntries.createFirst': 'Create your first journal entry',
    'track.journalEntries.newJournalEntry': 'New Journal Entry',
    'track.journalEntries.recordThoughts': 'Record your thoughts, feelings, and experiences',
    'track.journalEntries.nameEntry': 'Name your entry',
    'track.journalEntries.writeHere': 'Write your thoughts here...',

    // Daily activities dialog
    'track.dailyActivities.title': 'Daily Activities',
    'track.dailyActivities.description': 'Your personal wellness activities',
    'track.dailyActivities.empty': 'No daily activities yet',
    'track.dailyActivities.customizeYourActivities': 'Customize your activities',
    'track.dailyActivities.customizeActivities': 'Customize activities',

    // Emotion tracking
    'track.emotionTracking.title': 'Emotion Tracking',
    'track.emotionTracking.subtitle': 'Your emotional journey over time',
    'track.emotionTracking.empty': 'No emotion tracking entries yet',
    
    // Fatigue levels
    'track.fatigueLevels.title': 'Fatigue Levels',
    'track.fatigueLevels.subtitle': 'Your energy levels over time',
    'track.fatigueLevels.empty': 'No fatigue tracking entries yet',

    // Task customizer modal
    'tasks.customizer.title': 'Customize your daily acts',
    'tasks.customizer.subtitle':
      'Add, remove, or reorder the small actions that support your wellbeing.',
    'tasks.customizer.add': 'Add new act',
    'tasks.customizer.placeholder':
      'Write a small action that feels supportive for you',
    'tasks.customizer.empty': 'No custom acts yet',

    // Common UI (extended)
    'common.unknown': 'Unknown',
    'common.unknownTime': 'Unknown time',
    'common.close': 'Close',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.edit': 'Edit',
    'common.updated': 'Updated',
    'common.noContent': 'Nothing here yet',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.save': 'Save',
    'common.saved': 'Saved',
    'common.saving': 'Saving…',
    'common.share': 'Share',
    'common.confirm': 'Confirm',
    'common.loading': 'Loading…',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.tryAgain': 'Try again',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.of': 'of',
    'common.done': 'Done',
    'common.doIt': 'Do it',
    'common.complete': 'complete',
    'common.viewDetails': 'View details',
    'common.viewAll': 'View all',
    'common.customize': 'Customize',

    // Header / navigation
    'nav.home': 'Home',
    'nav.community': 'Community',
    'nav.achievements': 'Achievements',
    'nav.messages': 'Messages',
    'nav.profile': 'Profile',
    'nav.settings': 'Settings',

    // Generic form labels / helpers
    'form.required': 'Required',
    'form.optional': 'Optional',
    'form.search': 'Search',
    'form.placeholder.search': 'Search…',

    // Dialog / modal generic
    'dialog.title.default': 'Attention',
    'dialog.body.unsavedChanges':
      'You have unsaved changes. Are you sure you want to leave?',
    'dialog.confirmLeave': 'Leave without saving',
    'dialog.stay': 'Stay on this page',

    // Age gate modal
    'ageGate.title': 'Age confirmation',
    'ageGate.subtitle':
      'Alento is intended for people aged 16 or older. Please confirm your age.',
    'ageGate.label': 'I confirm that I am at least 16 years old',
    'ageGate.cta': 'Continue',
    'ageGate.exit': 'Exit app',

    // Panic mode modal
    'panic.title': 'Crisis support',
    'panic.subtitle':
      'If you are in immediate danger or thinking about harming yourself, please seek urgent help.',
    'panic.actions.callEmergency': 'Call emergency services',
    'panic.actions.viewResources': 'View crisis resources',
    'panic.actions.stayHere': 'Stay in the app',

    // Mood tracker modal
    'moodTracker.title': 'How are you feeling?',
    'moodTracker.subtitle': 'Your emotional state',
    'moodTracker.low': 'Low',
    'moodTracker.high': 'High',
    'moodTracker.difficult': 'Difficult',
    'moodTracker.mixed': 'Mixed',
    'moodTracker.neutral': 'Neutral',
    'moodTracker.hopeful': 'Hopeful',
    'moodTracker.strong': 'Strong',
    'moodTracker.energyLevel': 'Energy level',
    'moodTracker.exhausted': 'Exhausted',
    'moodTracker.tired': 'Tired',
    'moodTracker.average': 'Average',
    'moodTracker.focused': 'Focused',
    'moodTracker.energetic': 'Energetic',
    'moodTracker.addNotes': 'Add notes (optional)',
    'moodTracker.experienceToday': 'How would you describe your experience today?',
    'moodTracker.yourEnergyLevel': 'Your energy level',
    'moodTracker.mustBeLoggedIn': 'You must be logged in to track your mood.',
    'moodTracker.moodRecorded': 'Your mood has been recorded.',
    'moodTracker.failedToSave': 'Failed to save your mood. Please try again.',

    // Mood intensity labels
    'moodLabel.veryLow': 'Very low',
    'moodLabel.low': 'Low',
    'moodLabel.neutral': 'Neutral',
    'moodLabel.good': 'Good',
    'moodLabel.excellent': 'Excellent',

    // Fatigue intensity labels
    'fatigueLabel.exhausted': 'Exhausted',
    'fatigueLabel.veryTired': 'Very tired',
    'fatigueLabel.moderate': 'Moderate',
    'fatigueLabel.minimal': 'Energetic',

    // Daily tasks
    'dailyTasks.stretch.title': '5-minute gentle stretch',
    'dailyTasks.stretch.description': "Supports your body's recovery",
    'dailyTasks.positiveMoment.title': 'Write down one positive moment',
    'dailyTasks.positiveMoment.description': 'Helps build resilience',
    'dailyTasks.breathing.title': '3-minute mindful breathing',
    'dailyTasks.breathing.description': 'Reduces stress hormones',
    'dailyTasks.extraWater.title': 'Drink an extra glass of water',
    'dailyTasks.extraWater.description': 'Improves treatment effectiveness',
    'dailyTasks.walkOutside.title': '10-minute walk outside',
    'dailyTasks.walkOutside.description':
      'Boosts immune function and vitamin D',
    'dailyTasks.callFriend.title': 'Call a supportive friend',
    'dailyTasks.callFriend.description':
      'Social connection reduces inflammation',
    'dailyTasks.colorfulVeg.title': 'Eat a colorful vegetable',
    'dailyTasks.colorfulVeg.description':
      'Antioxidants support cell repair',
    'dailyTasks.calmingMusic.title': 'Listen to calming music',
    'dailyTasks.calmingMusic.description':
      'Reduces cortisol and blood pressure',
    'dailyTasks.gratitude.title': 'Practice gratitude for 2 minutes',
    'dailyTasks.gratitude.description':
      'Improves cellular immune response',
    'dailyTasks.medications.title': 'Take medications as prescribed',
    'dailyTasks.medications.description':
      'Maximizes treatment effectiveness',
    'dailyTasks.selfMassage.title': 'Self-massage for 3 minutes',
    'dailyTasks.selfMassage.description':
      'Improves lymphatic circulation',
    'dailyTasks.recordFeeling.title': "Record how you're feeling today",
    'dailyTasks.recordFeeling.description':
      'Emotional processing supports healing',

    // Welcome tutorial modal
    'welcome.title': 'Welcome to Alento',
    'welcome.subtitle':
      'A gentle space to track how you feel, find support, and practice small acts of self-care.',
    'welcome.step1.title': 'Check in with your mood',
    'welcome.step1.body':
      'Record how you feel during the day to notice patterns and triggers.',
    'welcome.step2.title': 'Practice small acts',
    'welcome.step2.body':
      'Choose simple, realistic actions that support your mental health.',
    'welcome.step3.title': 'Feel less alone',
    'welcome.step3.body':
      'Share and read reflections from others in the community whenever you need it.',
    'welcome.cta.getStarted': 'Get started',

    // Notifications / toasts
    'toast.saved': 'Changes saved successfully',
    'toast.deleted': 'Item deleted',
    'toast.error': 'Unable to complete this action',
    'toast.copied': 'Copied to clipboard',

    // Specific error messages
    'error.failedUpdateTaskCompletionStatus': 'Failed to update task completion status',
    'error.failedFetchAvailableTasks': 'Failed to fetch available tasks',
    'error.failedCustomizeDailyTasks': 'Failed to customize daily tasks',
    'error.failedAddCustomActivity': 'Failed to add custom activity',
    
    // Specific success messages
    'success.dailyActivitiesUpdated': 'Your daily activities have been updated',
    'success.customActivityAdded': 'Your custom activity has been added',

    // Privacy / GDPR page (keep last)
    'privacy.pageTitle': 'Alento - GDPR / RGPD Privacy Policy',
    'privacy.headerTitle': 'Privacy Policy (GDPR / RGPD)',
    'privacy.headerSubtitle':
      'How we collect, use, store, protect, and share your data',
    'privacy.lastUpdated': 'Last updated: January 2026',

    'privacy.section1Title': '1. Data Controller',
    'privacy.section1Body1':
      'The app is operated by Corvo Zen Lda, Cascais, Portugal, which acts as the data controller under GDPR / RGPD.',
    'privacy.section1Body2':
      'Corvo Zen Lda is responsible for determining how and why personal data is processed.',
    'privacy.section1ContactLabel': 'Contact:',
    'privacy.section1Email': 'privacy@corvozen.com',

    'privacy.section2Title': '2. Personal Data We Process',
    'privacy.section2Body1':
      'We process only the data strictly necessary to operate the app.',
    'privacy.section2Body2':
      'This includes account data, health-related inputs you choose to share, and technical/usage data such as device type and app diagnostics.',

    'privacy.section3Title': '3. Purpose of Processing',
    'privacy.section3Body1':
      'Personal data is processed solely to provide and operate the app, enable symptom tracking and personal insights, maintain security and reliability, respond to support requests, and improve functionality and user experience.',
    'privacy.section3Body2':
      'The app does not provide automated medical diagnoses and does not replace professional medical advice.',

    'privacy.section4Title': '4. Legal Basis',
    'privacy.section4Body1':
      'Processing is based on user consent for health data, contractual necessity to provide the app, and legitimate interests limited to security, stability, and improvement of the service.',
    'privacy.section4Body2':
      'Consent may be withdrawn at any time through the app or by contacting us.',

    'privacy.section5Title': '5. Data Sharing',
    'privacy.section5Body1': 'We do not sell personal or health data.',
    'privacy.section5Body2':
      'Data may be processed by essential service providers (such as hosting and crash reporting) acting strictly under our instructions and bound by data protection agreements.',

    'privacy.section6Title': '6. International Transfers',
    'privacy.section6Body1':
      'Where data is processed outside the European Economic Area, appropriate GDPR safeguards are applied, including Standard Contractual Clauses where required.',

    'privacy.section7Title': '7. Data Retention',
    'privacy.section7Body1':
      'Data is retained only while the user account is active or where required to comply with legal obligations.',
    'privacy.section7Body2':
      'Users may request deletion of their data at any time.',

    'privacy.section8Title': '8. User Rights',
    'privacy.section8Body1':
      'Under GDPR, users have the right to access their data, correct inaccurate data, request deletion, restrict or object to processing, and withdraw consent at any time.',
    'privacy.section8Body2':
      'Users can also lodge a complaint with the Portuguese Data Protection Authority (CNPD).',
    'privacy.section8CnpdLabel': 'CNPD:',
    'privacy.section8CnpdUrl': 'https://www.cnpd.pt',

    'privacy.section9Title': '9. Security',
    'privacy.section9Body1':
      'We implement appropriate technical and organisational measures to protect personal data, including encryption and access controls.',

    'privacy.section10Title': '10. Children',
    'privacy.section10Body1':
      'The app is not intended for users under 16 years of age without parental or legal guardian consent.',

    'privacy.section11Title': '11. Changes to This Policy',
    'privacy.section11Body1':
      'This policy may be updated to reflect legal or technical changes. Material changes will be communicated through the app.',

    'privacy.section12Title': '12. Contact',
    'privacy.section12Body1':
      'Privacy-related requests or questions may be sent to:',
    'privacy.section12Email': 'privacy@corvozen.com',
    'privacy.deleteAccountTitle': 'Delete Your Account',
    'privacy.deleteAccountBody':
      "You can delete your account and associated personal data at any time from here. This action is permanent and cannot be undone.",
    'privacy.deleteConfirm': "Are you sure you want to permanently delete your account?",
    'privacy.deleteSuccess': 'Your account has been deleted. You will be signed out now.',
    'privacy.deleteError': 'Failed to delete account. Please try again later.',
    'privacy.deleteButton': 'Permanently Delete Account',
  
    'achievements.resilience.1.title': 'Resilient Spirit',
    'achievements.resilience.1.description':
      'You faced and logged a difficult day but maintained your activities.',
    'achievements.resilience.2.title': 'Inner Strength',
    'achievements.resilience.2.description':
      'You completed your daily activities despite reporting high fatigue levels.',
    'achievements.resilience.3.title': 'Unbreakable',
    'achievements.resilience.3.description':
      'You maintained your mood tracking for a full week during treatment.',

    'achievements.consistency.1.title': 'Mindful Tracker',
    'achievements.consistency.1.description':
      'You tracked your mood for 3 consecutive days.',
    'achievements.consistency.2.title': 'Steady Progress',
    'achievements.consistency.2.description':
      'You completed all your daily activities for 5 consecutive days.',
    'achievements.consistency.3.title': 'Habit Master',
    'achievements.consistency.3.description':
      'You maintained a streak of journaling for 10 days straight.',

    'achievements.milestone.1.title': 'Journey Begins',
    'achievements.milestone.1.description':
      'You created your first journal entry.',
    'achievements.milestone.2.title': 'Halfway Mark',
    'achievements.milestone.2.description':
      "You've reached the halfway point in your treatment plan.",
    'achievements.milestone.3.title': 'Treatment Champion',
    'achievements.milestone.3.description':
      "You've completed your full treatment course.",

    'achievements.improvement.1.title': 'Better Days',
    'achievements.improvement.1.description':
      'Your mood tracking shows improvement over a 7-day period.',
    'achievements.improvement.2.title': 'Energy Revival',
    'achievements.improvement.2.description':
      'Your fatigue levels decreased for 5 consecutive days.',
    'achievements.improvement.3.title': 'Transformed',
    'achievements.improvement.3.description':
      "You've maintained excellent mood and energy levels for 14 days.",
    'achievements.desription': 'Description',
    'achievements.earnedOn': 'Earned on',
    'achievements.howToEarn': 'How to Earn',
    'achievements.howToEarnDetails': 'Continue your healing journey and track your progress to unlock this achievement.',
    'achievements.backToAllBadges': 'Back to all badges',
  },

  pt: {
    'header.crisis': 'Recursos de crise',
    'header.support': 'Apoio',
    'i18n.lang': 'Idioma',
    'tabs.presence': 'Presença',
    'tabs.pinboard': 'Quadro',
    'tabs.bookmarks': 'Guardados',

    // Home page
    'home.hello': 'Olá',
    'home.friend': 'Amigo',
    'home.howAreYouToday': 'Como estás hoje?',
    'home.howAreYouFeeling': 'Como te sentes?',
    'home.breatheNow': 'Respira agora',
    'home.trackMood': 'Regista o humor',
    'home.trackNow': 'Regista agora',
    'home.lastRecorded': 'Última vez registada',
    'home.feelingHopeful': 'Sinto-me esperançoso',
    'home.feelingNeutral': 'Sinto-me neutro',
    'home.havingToughDay': 'Tenho um dia difícil',
    'home.updated': 'Atualizado',
    'time.justNow': 'Agora mesmo',
    'time.minutesAgo': 'minutos atrás',
    'time.hoursAgo': 'horas atrás',
    'time.daysAgo': 'dias atrás',
    'time.weeksAgo': 'semanas atrás',
    'time.monthsAgo': 'meses atrás',
    'time.yearsAgo': 'anos atrás',
    'home.noMoodYet': 'Nenhum humor registado ainda',
    'home.recordFirstMood': 'Regista o teu primeiro humor',
    'home.yourDailyActs': 'Teus atos diários',
    'home.customize': 'Personalizar',
    'home.complete': '{completed} de {total} concluídos',
    'home.todaysInspiration': 'Inspiração de hoje',
    'home.dailyContent': 'Conteúdo diário',
    'home.share': 'Partilhar',
    'home.needAMoment': 'Precisas de um momento?',
    'home.instantRelief': 'Alívio instantâneo',
    'home.feelingOverwhelmed':
      'Estás a sentir-te sobrecarregado? Faz uma pausa rápida com respiração guiada.',
    'home.breatheWithMe': 'Respira comigo',
    'home.quoteCopiedToClipboard': 'Citação copiada para a área de transferência',

    // Panic mode / breathing exercise
    'panic.breatheWithMe': 'Respira comigo',
    'panic.focusOnBreathing': 'Foca-te na tua respiração',
    'panic.inhale': 'Inspira',
    'panic.exhale': 'Expira',
    'panic.progress': 'PROGRESSO',
    'panic.breaths': 'respirações',
    'panic.thisMoment': 'Este momento é teu',
    'panic.safeAndSupported': 'Estás seguro e apoiado',
    'panic.feelBetterNow': 'Sinto-me melhor agora',

    // Community page
    'community.whosHere': 'Quem está aqui?',
    'community.feelComfort': 'Sente o conforto de outros que entendem pelo que estás a passar, não importa a hora do dia.',
    'community.expresGratitude': 'Expressa Gratidão',
    'community.gratitudeHelps': 'Encontrar momentos de gratidão pode ajudar a mudar a tua perspetiva. Envia um agradecimento virtual para alguém que possa precisá-lo hoje.',
    'community.readAndConnect': 'Lê e conecta-te com reflexões de pessoas perto da tua localização.',
    'community.nearbyReflections': 'Reflexões Perto de Ti',
    'community.globalReflections': 'Reflexões Globais',
    'community.anonymousDisclaimer': 'Todo o conteúdo na comunidade é anónimo por defeito',
    'community.sendAnonymousGratitude': 'Envia Gratidão Anónima',
    'community.sendGratitude': 'Envia Gratidão',
    'community.selectSomeoneNearby': 'Seleciona Alguém Perto',
    'community.gratitudeBanner': 'Sempre que envias gratidão, alguém na tua região receberá uma notificação de que foi apreciado.',
    'community.noNearbyUsers': 'Nenhum utilizador perto encontrado. A tua gratidão será enviada anonimamente para alguém na comunidade global.',
    'community.anonymousUser': 'Utilizador Anónimo',
    'community.anonymousUserAvatar': 'Avatar de Utilizador Anónimo',
    'community.lastActive': 'Ativo pela última vez',
    'community.selected': 'Selecionado',
    'community.gratitudeMessage': 'Adiciona uma mensagem personalizada de gratidão (opcional)',
    'community.gratitudeForLike': 'Alguém apreciou a tua reflexão. A tua partilha importa e tocou alguém hoje.',
    'community.everyTimeYouSendGratitude': 'Sempre que envias gratidão, alguém na tua região receberá uma notificação de que foi apreciado.',
    'community.defaultGratitudeMessage': 'Obrigado por seres parte desta comunidade. Tua presença faz a diferença.',
    'community.hide': 'Ocultar',
    'community.loadingNearbyUsers': 'A carregar utilizadores próximos...',
    'community.sending': 'A enviar...',
    'community.shareYourThoughts': 'Partilha os Teus Pensamentos',
    'community.shareSpace': 'Este é um espaço para partilhar reflexões, fazer perguntas ou simplesmente expressar o que está na tua mente.',
    'community.placeholder': 'O que está a pensar hoje? Partilha os teus pensamentos com a comunidade...',
    'community.characters': 'caracteres',
    'community.moodPrompt': 'Seleciona um ou mais estados de espírito que descrevem como te sentes',
    'community.postReflection': 'Publica uma Reflexão',
    'community.currentReflections': 'Reflexões Atuais',
    'community.connectNearby': 'Conecta-te com pessoas na tua área que estão a passar por experiências semelhantes.',
    'community.reflectionsNearYou': 'Reflexões Perto de Ti',
    'community.savedReflections': 'Tuas Reflexões Guardadas',
    'community.savedSubtitle': 'Reflexões que guardaste para mais tarde. Guarda publicações que ressoam contigo para as rever.',
    'community.bookmarkedReflections': 'Reflexões Guardadas',
    'community.loadingReflections': 'A carregar reflexões...',
    'community.loadingNearbyReflections': 'A carregar reflexões próximas...',
    'community.loadingBookmarks': 'A carregar reflexões guardadas...',
    'community.noPostsYet': 'Nenhuma publicação ainda. Sê o primeiro a partilhar!',
    'community.noNearbyYet': 'Ainda não há reflexões próximas.',
    'community.noNearbyLater': 'Nenhuma reflexão próxima encontrada. Tenta mais tarde.',
    'community.noBookmarks': 'Ainda não tens reflexões guardadas. Começa a guardar publicações que ressoam contigo!',
    'community.shareReflection': 'Partilha uma reflexão para ser o primeiro',
    'community.anonymous': 'Anónimo',
    'community.location': 'Localização',
    'community.like': 'Gosto',
    'community.bookmark': 'Marcador',
    'community.report': 'Denunciar',
    'community.saved': 'Guardado',
    'community.likePost': 'Gostar da publicação',
    'community.unlikePost': 'Não gostar da publicação',
    'community.addBookmark': 'Adicionar aos guardados',
    'community.removeBookmark': 'Remover dos guardados',
    'community.reportPost': 'Denunciar publicação',
    'community.reported': 'Denunciado',
    'community.reportThanks': 'Obrigado por manter o espaço acolhedor.',
    'community.reportFailed': 'Falha ao denunciar',
    'community.reportError': 'Por favor, tenta mais tarde.',
    'community.prev': 'Ant',
    'community.gratitudeSent': 'A tua gratidão foi enviada!',
    'community.gratitudeFailed': 'Falha ao enviar gratidão',
    'community.serverError': 'Erro do servidor. Por favor, tenta novamente.',
    'community.networkError': 'Erro de rede. Por favor, tenta novamente.',
    'community.failedToLoadNearby': 'Não foi possível carregar utilizadores próximos. Por favor, tenta novamente.',
    'community.justNow': 'Agora mesmo',
    'community.oneMinuteAgo': 'há 1 minuto',
    'community.minutesAgo': 'minutos atrás',
    'community.oneHourAgo': 'há 1 hora',
    'community.hoursAgo': 'horas atrás',
    'community.oneDayAgo': 'há 1 dia',
    'community.daysAgo': 'dias atrás',

    // Mood/Track page moods
    'mood.peaceful': 'Tranquilo',
    'mood.hopeful': 'Esperançoso',
    'mood.grateful': 'Grato',
    'mood.excited': 'Entusiasmado',
    'mood.motivated': 'Motivado',
    'mood.inspired': 'Inspirado',
    'mood.joyful': 'Alegre',
    'mood.proud': 'Orgulhoso',
    'mood.content': 'Contente',
    'mood.reflective': 'Reflexivo',
    'mood.curious': 'Curioso',
    'mood.relaxed': 'Relaxado',
    'mood.anxious': 'Ansioso',
    'mood.overwhelmed': 'Sobrecarregado',
    'mood.frustrated': 'Frustrado',
    'mood.lonely': 'Sozinho',
    'mood.melancholic': 'Melancólico',
    'mood.irritated': 'Irritado',
    'mood.calm': 'Calmo',
    'mood.neutral': 'Neutro',
    'mood.stressed': 'Stressado',
     
    // Track page 
    'track.journal.title.min': 'O título deve ter pelo menos 2 caracteres',
    'track.journal.title.max': 'O título não deve exceder 100 caracteres',
    'track.journal.content.min': 'O conteúdo deve ter pelo menos 5 caracteres',
    'track.journal.content.max': 'O conteúdo não deve exceder 1000 caracteres',
    'track.title': 'Acompanha a tua jornada',
    'track.subtitle': 'Vê o teu progresso ao longo do tempo',
    'track.noData': 'Ainda não há dados. Começa a acompanhar para ver o teu progresso.',
    'track.emotionLevel': 'Nível emocional',
    'track.emotionLevels': 'Níveis emocionais',
    'track.viewDetails': 'Ver detalhes',
    'track.fatigueLevel': 'Nível de fadiga',
    'track.fatigueLevels': 'Níveis de fadiga',
    'track.activityHistoryEmpty': 'Ainda não há histórico de atividades',

    // Track page Journal Entries
    'track.journalEntries.title': 'Entradas de Diário',
    'track.journalEntries.newEntry': 'Nova Entrada',
    'track.journalEntries.empty': 'Ainda não há entradas de diário',
    'track.journalEntries.journal': 'Diário',
    'track.journalEntries.dialogTitle': 'Partilhar entrada de diário',
    'track.journalEntries.viewAll': 'Ver todas as entradas',
    'track.journalEntries.description': 'As tuas reflexões e notas pessoais',
    'track.journalEntries.copiedAlert': 'Entrada de diário copiada para a área de transferência',
    'track.journalEntries.createFirst': 'Cria a tua primeira entrada de diário',
    'track.journalEntries.newJournalEntry': 'Nova Entrada',
    'track.journalEntries.recordThoughts': 'Regista os teus pensamentos, sentimentos e experiências',
    'track.journalEntries.nameEntry': 'Nomeia a tua entrada',
    'track.journalEntries.writeHere': 'Escreve os teus pensamentos aqui...',
    'track.journalEntries.saveEntry': 'Guardar entrada',

    // Track page Achievements & Resilience badges
    'track.achievements.title': 'Conquistas e Resiliência',
    'track.achievements.recentlyEarned': 'Conquistas recentemente obtidas',
    'track.achievements.earned': 'obtidas',
    'track.achievments.recentlyEarned': 'Obtidas recentemente',
    'track.achievements.empty': 'Ainda não tens ganhado conquistas. Continua a acompanhar a tua jornada para desbloqueares conquistas!',
    'track.achievements.nextToUnlock': 'Próximas a desbloquear',
    'track.achievements.viewAllBadges': 'Ver todas as insígnias',
    'track.achievements.dialogTitle': 'Conquistas e Insígnias',
    'track.achievements.dialogDescription': 'A tua jornada é acompanhada através destas conquistas que representam a tua resiliência e progresso.',
    'track.achievements.resilienceBadges': 'Insígnias de Resiliência',
    'track.achievements.consistencyBadges': 'Insígnias de Consistência',
    'track.achievements.milestoneBadges': 'Insígnias de Marco',
    'track.achievements.improvementBadges': 'Insígnias de Melhoria',

    // Daily activities dialog
    'track.dailyActivities.title': 'Atividades Diárias',
    'track.dailyActivities.history': 'Histórico de Atividades',
    'track.dailyActivities.description': 'As tuas atividades pessoais de bem-estar',
    'track.dailyActivities.empty': 'Ainda não há atividades diárias',
    'track.dailyActivities.customizeYourActivities': 'Personaliza as tuas atividades',
    'track.dailyActivities.customizeActivities': 'Personalizar atividades',

    // Track page today's activities
    'track.todayActivities.title': 'Atividades de hoje',
    'track.todayActivities.empty': 'Ainda não há atividades',
    'track.todayActivities.customize': 'Personaliza as tuas atividades',
    'track.todayActivities.viewAll': 'Ver todas as atividades',

    // Emotion tracking
    'track.emotionTracking.title': 'Acompanhamento Emocional',
    'track.emotionTracking.subtitle': 'A tua jornada emocional ao longo do tempo',
    'track.emotionTracking.empty': 'Ainda não há entradas de acompanhamento emocional',
    'track.emotionTracking.details': 'Acompanhamento Emocional',

    // Fatigue levels
    'track.fatigueLevels.title': 'Níveis de Fadiga',
    'track.fatigueLevels.subtitle': 'Os teus níveis de energia ao longo do tempo',
    'track.fatigueLevels.empty': 'Ainda não há entradas de acompanhamento de fadiga',
    'track.fatigueLevels.details': 'Nivéis de Fadiga',

    // Task customizer modal
    'taskCustomizer.title': 'Personaliza os teus atos diários',
    'taskCustomizer.subtitle':
        'Adiciona, remove ou reorganiza as pequenas ações que apoiam o teu bem-estar.',
    'taskCustomizer.add': 'Adicionar novo ato',
    'taskCustomizer.placeholder':
        'Escreve uma pequena ação que te pareça útil',
    'taskCustomizer.empty': 'Ainda não tens atos personalizados',
    'taskCustomizer.customizeYourDailyActivities': 'Personaliza as tuas atividades diárias',
    'taskCustomizer.info.1': 'Escolhe até 5 atividades que serão mais benéficas para a tua recuperação.',
    'taskCustomizer.info.2': 'Podes selecionar entre atividades recomendadas ou criar as tuas próprias atividades personalizadas.',
    'taskCustomizer.recommendedActivities': 'Atividades recomendadas',
    'taskCustomizer.createYourOwn': 'Cria as tuas',
    'taskCustomizer.createACustomActivity': 'Cria uma atividade personalizada',
    'taskCustomizer.activitiesSelected': 'atividades selecionadas',
    'taskCustomizer.activities': 'atividades',
    'taskCustomizer.saveActivities': 'Guardar',
    'taskCustomizer.addActivity': 'Adicionar atividade',
    'taskCustomizer.activityTitle': 'Título da atividade',
    'taskCustomizer.activityTitlePlaceholder': 'Ex.: Tocar piano',
    'taskCustomizer.whyThisActivityHelpsYou': 'Por que é que esta atividade te ajuda?',
    'taskCustomizer.activityDescriptionPlaceholder':
        'Ex.: A música ajuda-me a relaxar e a expressar emoções',

    // Resources page
    'resources.title': 'Recursos de Apoio',
    'resources.subtitle': 'Supporte e comunidade',
    'resources.soundTherapy.title': 'Terapia de Som',
    'resources.soundTherapy.subtitle': 'Frequências curativas e sons',
    'resources.dailyTips.title': 'Dicas Diárias',
    'resources.dailyTips.subtitle': 'Stratégias baseadas em PNI',
    'resources.privacyTerms.title': 'Privacidade e Termos',
    'resources.privacyTerms.subtitle': 'Como nós protegemos os teus dados',
    'resources.crisisSupport.title': 'Recursos de Crise',
    'resources.crisisSupport.subtitle': 'Emergência médica: vai ao hospital mais próximo; para apoio, vê as linhas',
    
    // Sound therapy page
    'soundTherapy.title': 'Terapia de Som',
    'soundTherapy.subtitle': 'Frequências curativas e tons',
    'soundTherapy.description': 'Frequências curativas e batidas binaurais',
    'soundTherapy.allMeditations': 'Todas as Meditações',

    // Daily tips page
    'dailyTips.title': 'Dicas Diárias',
    'dailyTips.subtitle': 'Estratégias baseadas em PNI',
    'dailyTips.description': 'Estratégias baseadas em PNI para a tua jornada de recuperação',
    'dailyTips.todaysTip': 'Dica de hoje',
    'dailyTips.allTips': 'Todas as Dicas',
    'dailyTips.source': 'Fonte',

    // Profile page
    'profile.logoutConfirm': 'Tens a certeza de que queres terminar sessão?',
    'profile.updateSoundSettingsFailed':
      'Não foi possível atualizar as definições de som.',
    'profile.saveSoundSettingsFailed':
      'Não foi possível guardar as definições de som.',
    'profile.soundEffectsEnabled': 'Efeitos sonoros ativados',
    'profile.soundEffectsDisabled': 'Efeitos sonoros desativados',
    'profile.soundEffectsNowPlay': 'Os efeitos sonoros agora vão tocar durante as exercícios de respiração e notificações.',
    'profile.soundEffectsTurnedOff':
      'Os efeitos sonoros foram desativados.',
    'profile.updateRemindersSettingsFailed':
      'Não foi possível atualizar as definições de lembretes.',
    'profile.saveRemindersSettingsFailed':
      'Não foi possível guardar as definições de lembretes.',
    'profile.dailyRemindersEnabled': 'Lembretes diários ativados',
    'profile.dailyRemindersDisabled': 'Lembretes diários desativados',
    'profile.dailyRemindersNowOn':
      'Os lembretes diários agora estão ativados para te ajudar a acompanhar as tuas atividades e humor.',
    'profile.dailyRemindersTurnedOff':
      'Os lembretes diários foram desativados.',
    'profile.failedToUpdateLanguageSetting':
        'Não foi possível atualizar a definição de idioma.',
    'profile.failedToSaveLanguageSetting':
        'Não foi possível guardar a definição de idioma.',
    'profile.exportDataFailed':
      'Desculpa, não foi possível exportar os teus dados neste momento. Por favor, tenta novamente mais tarde.',
    'profile.title': 'O teu Perfil',
    'profile.subtitle': 'Definições da conta e preferências',
    'profile.editProfile': 'Editar Perfil',
    'profile.notifications': 'Notificações',
    'profile.privacySecurity': 'Privacidade e Segurança',
    'profile.language': 'Idioma',
    'profile.logOut': 'Terminar Sessão',
    'profile.appPreferences': 'Preferências da App',
    'profile.darkMode': 'Modo Escuro',
    'profile.darkModeEnabled': 'Modo escuro ativado',
    'profile.lightModeEnabled': 'Modo claro ativado',
    'profile.darkModeNowOn': 'O modo escuro agora está ativado para uma experiência mais confortável à noite.',
    'profile.lightModeNowOn': 'O modo claro agora está ativado para uma experiência mais brilhante.',
    'profile.easierOnEyes': 'Mais fácil para os olhos à noite',
    'profile.soundEffects': 'Efeitos Sonoros',
    'profile.forBreathingExercises': 'Para exercícios de respiração',
    'profile.dailyReminders': 'Lembretes Diários',
    'profile.forTrackingAndActivities': 'Para acompanhamento e atividades',
    'profile.dataManagement': 'Gestão de Dados',
    'profile.exportingData': 'A exportar os teus dados…',
    'profile.exportYourData': 'Exportar os Teus Dados',
    'profile.deleteAccountConfirm': 'Tens a certeza de que queres eliminar permanentemente a tua conta? Esta ação não pode ser desfeita.',
    'profile.accountDeleted': 'Conta eliminada com sucesso. Vais ser redirecionado para a página inicial.',
    'profile.deleteAccount': 'Eliminar Conta',
    'profile.deleteAccountFailed': 'Não foi possível eliminar a conta. Tenta novamente mais tarde.',
    'profile.madeWith': 'feito com',

    // Edit Profile page
    'editProfile.title': 'Editar Perfil',
    'editProfile.subtitle': 'Atualiza as tuas informações pessoais',
    'editProfile.fullName': 'Nome Completo',
    'editProfile.fullNamePlaceholder': 'O teu nome completo',
    'editProfile.username': 'Nome de Utilizador',
    'editProfile.usernamePlaceholder': 'O teu nome de utilizador',
    'editProfile.saveChanges': 'Guardar Alterações',

    // Notifications page
    'notifications.pageTitle': 'Notificações',
    'notifications.unread': 'não lidas',
    'notifications.allCaughtUp': 'Tudo em dia',
    'notifications.recent': 'Recentes',
    'notifications.markAllAsRead': 'Marcar todas como lidas',
    'notifications.cardTitle': 'Tuas Notificações',
    'notifications.noNotifications': 'Nenhumas notificações',
    'notifications.markAsRead': 'Marcar como lida',
    'notifications.delete': 'Eliminar',

    // Gratitude banner
    'gratitudeBanner.someOneSentYouGratitude': 'Alguém te enviou gratidão',
     
    // Notification dropdown
    'notifications.title': 'Notificações',
    'notifications.justNow': 'agora mesmo',
    'notifications.ago': 'atrás',
    
    // Bottom Nav bar
    'bottomNav.home': 'Início',
    'bottomNav.community': 'Comunidade',
    'bottomNav.track': 'Acompanha',
    'bottomNav.resources': 'Recursos',
    'bottomNav.profile': 'Perfil',

    // Common UI (extended)
    'common.unknown': 'Desconhecido',
    'common.unknownTime': 'Hora desconhecida',
    'common.close': 'Fechar',
    'common.back': 'Voltar',
    'common.next': 'Seguinte',
    'common.edit': 'Editar',
    'common.updated': 'Atualizado',
    'common.noContent': 'Nada aqui ainda',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.save': 'Guardar',
    'common.saved': 'Guardado',
    'common.saving': 'A guardar…',
    'common.share': 'Partilhar',
    'common.confirm': 'Confirmar',
    'common.loading': 'A carregar…',
    'common.error': 'Erro',
    'common.success': 'Sucesso',
    'common.tryAgain': 'Tentar novamente',
    'common.yes': 'Sim',
    'common.no': 'Não',
    'common.ok': 'OK',
    'common.of': 'de',
    'common.done': 'Feito',
    'common.doIt': 'Fazê-lo',
    'common.complete': 'concl.',
    'common.viewDetails': 'Ver detalhes',
    'common.viewAll': 'Ver tudo',
    'common.customize': 'Personalizar',

    // Header / navigation
    'nav.home': 'Início',
    'nav.community': 'Comunidade',
    'nav.achievements': 'Conquistas',
    'nav.messages': 'Mensagens',
    'nav.profile': 'Perfil',
    'nav.settings': 'Definições',

    // Generic form labels / helpers
    'form.required': 'Obrigatório',
    'form.optional': 'Opcional',
    'form.search': 'Pesquisar',
    'form.placeholder.search': 'Pesquisar…',

    // Dialog / modal generic
    'dialog.title.default': 'Atenção',
    'dialog.body.unsavedChanges':
      'Tens alterações por guardar. Tens a certeza de que queres sair?',
    'dialog.confirmLeave': 'Sair sem guardar',
    'dialog.stay': 'Ficar nesta página',

    // Age gate modal
    'ageGate.title': 'Confirmação de idade',
    'ageGate.subtitle':
      'A Alento destina-se a pessoas com 16 anos ou mais. Confirma a tua idade, por favor.',
    'ageGate.label': 'Confirmo que tenho pelo menos 16 anos',
    'ageGate.cta': 'Continuar',
    'ageGate.exit': 'Sair da app',

    // Panic mode modal
    'panic.title': 'Apoio em crise',
    'panic.subtitle':
      'Se estás em perigo imediato ou a pensar magoar-te, procura ajuda urgente.',
    'panic.actions.callEmergency': 'Ligar para os serviços de emergência',
    'panic.actions.viewResources': 'Ver recursos de crise',
    'panic.actions.stayHere': 'Ficar na app',

    // Mood tracker modal
    'moodTracker.title': 'Como te estás a sentir?',
    'moodTracker.subtitle': 'O teu estado emocional',
    'moodTracker.low': 'Baixo',
    'moodTracker.high': 'Alto',
    'moodTracker.difficult': 'Difícil',
    'moodTracker.mixed': 'Misto',
    'moodTracker.neutral': 'Neutro',
    'moodTracker.hopeful': 'Esperançoso',
    'moodTracker.strong': 'Forte',
    'moodTracker.energyLevel': 'Nível de energia',
    'moodTracker.exhausted': 'Exausto',
    'moodTracker.tired': 'Cansado',
    'moodTracker.average': 'Médio',
    'moodTracker.focused': 'Focado',
    'moodTracker.energetic': 'Energético',
    'moodTracker.addNotes': 'Adicionar notas (opcional)',
    'moodTracker.experienceToday': 'Como descreverias a tua experiência hoje?',
    'moodTracker.yourEnergyLevel': 'O teu nível de energia',
    'moodTracker.mustBeLoggedIn':
      'Tens de iniciar sessão para acompanhar o teu humor.',
    'moodTracker.moodRecorded': 'O teu humor foi registado.',
    'moodTracker.failedToSave':
      'Não foi possível guardar o teu humor. Por favor, tenta novamente.',

    // Daily tasks
    'dailyTasks.stretch.title': 'Alongamento suave de 5 minutos',
    'dailyTasks.stretch.description': 'Apoia a recuperação do teu corpo',
    'dailyTasks.positiveMoment.title': 'Escreve um momento positivo',
    'dailyTasks.positiveMoment.description': 'Ajuda a construir resiliência',
    'dailyTasks.breathing.title': 'Respiração consciente de 3 minutos',
    'dailyTasks.breathing.description': 'Reduz os hormônios do stress',
    'dailyTasks.extraWater.title': 'Bebe um copo extra de água',
    'dailyTasks.extraWater.description':
      'Melhora a eficácia do tratamento',
    'dailyTasks.walkOutside.title': 'Caminhada de 10 minutos ao ar livre',
    'dailyTasks.walkOutside.description':
      'Estimula a função imunitária e a vitamina D',
    'dailyTasks.callFriend.title': 'Liga a um amigo de apoio',
    'dailyTasks.callFriend.description':
      'A conexão social reduz a inflamação',
    'dailyTasks.colorfulVeg.title': 'Come um vegetal colorido',
    'dailyTasks.colorfulVeg.description':
      'Os antioxidantes apoiam a reparação celular',
    'dailyTasks.calmingMusic.title': 'Ouve música calmante',
    'dailyTasks.calmingMusic.description':
      'Reduz o cortisol e a pressão arterial',
    'dailyTasks.gratitude.title': 'Pratica gratidão por 2 minutos',
    'dailyTasks.gratitude.description':
      'Melhora a resposta imunitária celular',
    'dailyTasks.medications.title': 'Toma os medicamentos conforme prescrito',
    'dailyTasks.medications.description':
      'Maximiza a eficácia do tratamento',
    'dailyTasks.selfMassage.title': 'Auto-massagem por 3 minutos',
    'dailyTasks.selfMassage.description':
      'Melhora a circulação linfática',
    'dailyTasks.recordFeeling.title': 'Regista como te sentes hoje',
    'dailyTasks.recordFeeling.description':
      'O processamento emocional apoia a cura',
    
    // Welcome tutorial modal
    'welcome.title': 'Bem-vindo à Alento',
    'welcome.subtitle':
      'Um espaço tranquilo para acompanhar como te sentes, encontrar apoio e praticar pequenos atos de autocuidado.',
    'welcome.step1.title': 'Faz check-in ao teu humor',
    'welcome.step1.body':
      'Regista como te sentes ao longo do dia para notar padrões e gatilhos.',
    'welcome.step2.title': 'Pratica pequenos atos',
    'welcome.step2.body':
      'Escolhe ações simples e realistas que apoiem a tua saúde mental.',
    'welcome.step3.title': 'Sente-te menos sozinho',
    'welcome.step3.body':
      'Partilha e lê reflexões de outras pessoas na comunidade sempre que precisares.',
    'welcome.cta.getStarted': 'Começar',

    // Notifications / toasts
    'toast.saved': 'Alterações guardadas com sucesso',
    'toast.deleted': 'Item eliminado',
    'toast.error': 'Não foi possível concluir esta ação',
    'toast.copied': 'Copiado para a área de transferência',

    // Specific error messages
    'error.failedUpdateTaskCompletionStatus': 'Falha ao atualizar o estado da atividade',
    'error.failedFetchAvailableTasks': 'Falha ao carregar atividades disponíveis',
    'error.failedCustomizeDailyTasks': 'Falha ao personalizar atividades diárias',
    'error.failedAddCustomActivity': 'Falha ao adicionar atividade personalizada',
    
    // Specific success messages
    'success.dailyActivitiesUpdated': 'As tuas atividades diárias foram atualizadas',
    'success.customActivityAdded': 'A tua atividade personalizada foi adicionada',

    // Privacy / GDPR page (keep last)
    'privacy.pageTitle': 'Alento - Política de Privacidade RGPD',
    'privacy.headerTitle': 'Política de Privacidade (RGPD)',
    'privacy.headerSubtitle':
      'Como recolhemos, utilizamos, armazenamos, protegemos e partilhamos os teus dados',
    'privacy.lastUpdated': 'Última atualização: Janeiro de 2026',

    'privacy.section1Title': '1. Responsável pelo Tratamento',
    'privacy.section1Body1':
      'A app é operada pela Corvo Zen Lda, Cascais, Portugal, que atua como responsável pelo tratamento ao abrigo do RGPD.',
    'privacy.section1Body2':
      'A Corvo Zen Lda é responsável por determinar como e por que motivo os dados pessoais são tratados.',
    'privacy.section1ContactLabel': 'Contacto:',
    'privacy.section1Email': 'privacy@corvozen.com',

    'privacy.section2Title': '2. Dados Pessoais Tratados',
    'privacy.section2Body1':
      'Apenas tratamos os dados estritamente necessários ao funcionamento da app.',
    'privacy.section2Body2':
      'Isto inclui dados de conta, informações de saúde que decides partilhar e dados técnicos/de utilização como tipo de dispositivo e diagnósticos da app.',

    'privacy.section3Title': '3. Finalidades',
    'privacy.section3Body1':
      'Os dados são tratados exclusivamente para operar a app, apoiar o acompanhamento pessoal de sintomas, garantir segurança e estabilidade, responder a pedidos de apoio e melhorar a funcionalidade e a experiência do utilizador.',
    'privacy.section3Body2':
      'A app não fornece diagnósticos médicos automatizados nem substitui aconselhamento médico profissional.',

    'privacy.section4Title': '4. Fundamento Jurídico',
    'privacy.section4Body1':
      'O tratamento baseia-se no consentimento do utilizador para dados de saúde, na execução do serviço e em interesses legítimos limitados à segurança, estabilidade e melhoria do serviço.',
    'privacy.section4Body2':
      'O consentimento pode ser retirado a qualquer momento através da app ou contactando-nos.',

    'privacy.section5Title': '5. Partilha de Dados',
    'privacy.section5Body1': 'Não vendemos dados pessoais ou de saúde.',
    'privacy.section5Body2':
      'Os dados podem ser tratados por prestadores de serviços essenciais (como alojamento e relatórios de erro), ao abrigo de instruções nossas e de acordos de proteção de dados.',

    'privacy.section6Title': '6. Transferências Internacionais',
    'privacy.section6Body1':
      'Quando os dados são tratados fora do Espaço Económico Europeu, são aplicadas salvaguardas adequadas ao RGPD, incluindo Cláusulas Contratuais-Tipo quando exigido.',

    'privacy.section7Title': '7. Conservação',
    'privacy.section7Body1':
      'Os dados são conservados apenas enquanto a conta do utilizador estiver ativa ou enquanto forem necessários para cumprir obrigações legais.',
    'privacy.section7Body2':
      'O utilizador pode pedir a eliminação dos seus dados a qualquer momento.',

    'privacy.section8Title': '8. Direitos do Utilizador',
    'privacy.section8Body1':
      'Ao abrigo do RGPD, o utilizador pode exercer os seus direitos de acesso, retificação, eliminação, limitação, oposição ao tratamento e retirada de consentimento em qualquer momento.',
    'privacy.section8Body2':
      'O utilizador pode ainda apresentar reclamação junto da Comissão Nacional de Proteção de Dados (CNPD).',
    'privacy.section8CnpdLabel': 'CNPD:',
    'privacy.section8CnpdUrl': 'https://www.cnpd.pt',

    'privacy.section9Title': '9. Segurança',
    'privacy.section9Body1':
      'São aplicadas medidas técnicas e organizativas adequadas, incluindo encriptação e controlo de acessos, para proteger os dados pessoais.',

    'privacy.section10Title': '10. Menores',
    'privacy.section10Body1':
      'A app não se destina a menores de 16 anos sem consentimento parental ou de representante legal.',

    'privacy.section11Title': '11. Alterações',
    'privacy.section11Body1':
      'Esta política pode ser atualizada para refletir alterações legais ou técnicas. Alterações relevantes serão comunicadas através da app.',

    'privacy.section12Title': '12. Contacto',
    'privacy.section12Body1':
      'Pedidos ou questões relacionadas com privacidade podem ser enviados para:',
    'privacy.section12Email': 'privacy@corvozen.com',
    'privacy.deleteAccountTitle': 'Eliminar Tua Conta',
    'privacy.deleteAccountBody': "Podes eliminar a tua conta e os dados pessoais associados a qualquer momento a partir daqui. Esta ação é permanente e não pode ser desfeita.",
    'privacy.deleteConfirm': 'Tens a certeza de que queres eliminar a tua conta permanentemente?',
    'privacy.deleteSuccess': 'A tua conta foi eliminada com sucesso. Vais ser redirecionado para a página inicial agora.',
    'privacy.deleteError': 'Falha ao eliminar a conta. Por favor, tenta novamente mais tarde.',
    'privacy.deleteButton': 'Permanentemente Eliminar Conta',

    // Achievements translations
    'achievements.resilience.1.title': 'Espírito Resiliente',
    'achievements.resilience.1.description':
      'Enfrentaste e registaste um dia difícil mas mantiveste as tuas atividades.',
    'achievements.resilience.2.title': 'Força Interior',
    'achievements.resilience.2.description':
      'Completaste as tuas atividades diárias apesar de reportar níveis elevados de fadiga.',
    'achievements.resilience.3.title': 'Inquebrantável',
    'achievements.resilience.3.description':
      'Mantiveste o acompanhamento do teu humor durante uma semana completa durante o tratamento.',

    'achievements.consistency.1.title': 'Registo Atento',
    'achievements.consistency.1.description':
      'Registaste o teu humor durante 3 dias consecutivos.',
    'achievements.consistency.2.title': 'Progresso Estável',
    'achievements.consistency.2.description':
      'Completaste todas as tuas atividades diárias durante 5 dias consecutivos.',
    'achievements.consistency.3.title': 'Mestre do Hábito',
    'achievements.consistency.3.description':
      'Mantiveste uma sequência de diário durante 10 dias seguidos.',

    'achievements.milestone.1.title': 'A Jornada Começa',
    'achievements.milestone.1.description':
      'Criaste a tua primeira entrada de diário.',
    'achievements.milestone.2.title': 'Meio Caminho',
    'achievements.milestone.2.description':
      'Chegaste a meio do teu plano de tratamento.',
    'achievements.milestone.3.title': 'Campeão do Tratamento',
    'achievements.milestone.3.description':
      'Completaste o teu curso completo de tratamento.',

    'achievements.improvement.1.title': 'Dias Melhores',
    'achievements.improvement.1.description':
      'O teu acompanhamento de humor mostra melhorias ao longo de um período de 7 dias.',
    'achievements.improvement.2.title': 'Reviravolta de Energia',
    'achievements.improvement.2.description':
      'Os teus níveis de fadiga diminuíram durante 5 dias consecutivos.',
    'achievements.improvement.3.title': 'Transformado',
    'achievements.improvement.3.description':
      'Mantiveste excelentes níveis de humor e energia durante 14 dias.',
    'achievements.description': 'Descrição',
    'achievements.earnedOn': 'Obtida em',
    'achievements.howToEarn': 'Como Ganhar',
    'achievements.howToEarnDetails':
      'Continua a tua jornada de cura e acompanha o teu progresso para desbloqueares esta conquista.',
    'achievements.backToAllBadges': 'Voltar a todas as conquistas',
  },
};


// Inspiration quotes translations
export const inspirationTranslations = {
  pt: {
    0: { quote: "Estás autorizado a descansar. Estás autorizado a abrandar. Sê gentil contigo próprio.", author: "Dra. Sarah Chen, Psicóloga da Oncologia" },
    1: { quote: "Atos pequenos feitos consistentemente são mais fortes que arrebatamentos raros.", author: "Prática Diária PNI" },
    2: { quote: "O teu corpo ouve tudo o que a tua mente diz. Fala com gentileza hoje.", author: "Lembrança Mente-Corpo" },
    3: { quote: "Até dois minutos de respiração podem mudar a próxima hora.", author: "Prática de Respiração" },
    4: { quote: "A cura não é linear. O progresso pode ser silencioso e ainda assim existir.", author: "Nota de Compaixão" },
    5: { quote: "Pede menos a este dia. Recebe mais deste momento.", author: "Prática de Presença" },
    6: { quote: "Não estás sozinho. Muitos caminham a teu lado em espírito.", author: "Lembrança Comunitária" },
    7: { quote: "Deixa o teu sistema nervoso acalmar; a clareza segue a calma.", author: "Prática de Regulação" },
    8: { quote: "O repouso é produtivo quando a cura é o teu trabalho.", author: "Sabedoria de Cura" },
    9: { quote: "Enfrenta este dia um respiro, um passo, uma gentileza de cada vez.", author: "Ritmo Gentil" },
    10: { quote: "O teu valor não é medido pelo teu nível de energia.", author: "Lembrança de Auto-Compaixão" },
    11: { quote: "A cura acontece entre os esforços—honra o meio-termo.", author: "Perspetiva de Recuperação" },
    12: { quote: "Nota o que é constante: a tua respiração, o solo, a tua coragem.", author: "Prática de Ancoragem" },
    13: { quote: "Pergunta ao teu corpo do que precisa. Depois dá-lhe algo pequeno.", author: "Check-in Corporal" },
    14: { quote: "Está bem seres exatamente como és, agora mesmo.", author: "Autorização" },
    15: { quote: "Quando a gentileza encontra a disciplina, a cura acelera.", author: "Prática Equilibrada" },
    16: { quote: "Não estás atrasado. Estás no teu caminho.", author: "Lembrança de Ritmo" },
    17: { quote: "Deixa a alegria ser pequena e frequente, não rara e perfeita.", author: "Prática de Alegria" },
    18: { quote: "O teu eu futuro agradece-te por descansares hoje.", author: "Tu Futuro" },
    19: { quote: "O alívio cresce com o ritmo: respira, bebe água, faz uma pausa.", author: "Nota de Ritmo" },
    20: { quote: "Fala contigo próprio como falavas a um amigo querido.", author: "Prática de Compaixão" },
    21: { quote: "Ser mais suave é mais forte quando estás a curar.", author: "Força Gentil" },
    22: { quote: "Escolhe uma coisa fácil e deixa-a ser suficiente.", author: "Prática de Suficiência" },
    23: { quote: "Podes começar de novo em qualquer momento—este é um deles.", author: "Começar de Novo" },
    24: { quote: "A gratidão é combustível para a resiliência. Nomeia uma coisa agora.", author: "Combustível de Resiliência" },
    25: { quote: "Melhorias minúsculas acumulam-se; confia nas tuas pequenas vitórias.", author: "Cuidados que se Acumulam" },
    26: { quote: "Deixa este dia ser mais gentil que o anterior e mais corajoso que o próximo.", author: "Coragem Gentil" },
    27: { quote: "Ancora-te na respiração quando os pensamentos acelerram.", author: "Âncora de Respiração" },
    28: { quote: "Mereces cuidados sem teres de os ganhar.", author: "Valor Inato" },
    29: { quote: "Escolhe o repouso em vez da pressa; as tuas células agradecer-te-ão.", author: "Escolha Restauradora" },
    30: { quote: "A tua presença é suficiente para este momento.", author: "Lembrança de Presença" },
    31: { quote: "Um respiro cheio de amor. Repete.", author: "Prática Simples" },
  }
};

// Daily Tips translations (European Portuguese - informal tu form)
export const dailyTipsTranslations = {
  pt: {
    1: {
      title: "Prática de Respiração Profunda",
      description: "Reserva 5 minutos para respiração diafragmática profunda. Inspira durante 4 segundos, prende durante 2, expira durante 6. Isto ativa o teu sistema nervoso parassimpático, reduzindo cortisol e apoiando a função imunológica."
    },
    2: {
      title: "Diário de Gratidão",
      description: "Escreve três coisas pelas quais estás grato hoje. A investigação mostra que esta prática reduz biomarcadores inflamatórios e aumenta a atividade em regiões cerebrais associadas a emoções positivas."
    },
    3: {
      title: "Movimento Suave",
      description: "Mesmo durante o tratamento, procura fazer 10-15 minutos de movimento suave. Alongamentos leves ou caminhadas lentas promovem a circulação de células imunológicas e reduzem marcadores inflamatórios."
    },
    4: {
      title: "Alimentos Ricos em Proteína",
      description: "Inclui fontes de proteína de qualidade em cada refeição. A proteína fornece aminoácidos essenciais para a produção de células imunológicas e ajuda a manter a massa muscular durante o tratamento."
    },
    5: {
      title: "Horário Regular de Sono",
      description: "Tenta manter horários consistentes para dormir e acordar. Isto ajuda a regular o ritmo circadiano, que influencia diretamente a função das células imunológicas e respostas inflamatórias."
    },
    6: {
      title: "Alimentos Ricos em Ómega-3",
      description: "Inclui alimentos ricos em ácidos gordos ómega-3 como nozes, sementes de linho ou peixe gordo, se aprovado pelo teu médico. Estes modulam a inflamação e apoiam a saúde neurológica."
    },
    7: {
      title: "Conexão Social",
      description: "Passa tempo com pessoas que te apoiam, mesmo que brevemente. Interações sociais positivas podem reduzir citocinas pró-inflamatórias e promover a função das células T regulatórias."
    },
    8: {
      title: "Momentos de Atenção Plena",
      description: "Pratica atenção plena durante 3-5 minutos nas atividades diárias. Isto reduz marcadores inflamatórios e equilibra a atividade do sistema nervoso simpático e parassimpático."
    },
    9: {
      title: "Hidratação Adequada",
      description: "Certifica-te de que bebes água suficiente ao longo do dia. A hidratação adequada apoia a função linfática, permitindo que as células imunológicas se movam eficientemente pelo corpo."
    },
    10: {
      title: "Dieta Rica em Antioxidantes",
      description: "Inclui frutas e vegetais coloridos, se tolerado. Os antioxidantes ajudam a neutralizar radicais livres que podem danificar as células imunológicas e contribuir para a inflamação."
    },
    11: {
      title: "Ritmo Natural de Luz",
      description: "Recebe luz natural de manhã e limita luzes brilhantes à noite. Isto ajuda a regular a produção de melatonina, que tem propriedades imunomoduladoras e anti-inflamatórias."
    },
    12: {
      title: "Relaxamento Muscular Progressivo",
      description: "Tenta tensionar e depois relaxar cada grupo muscular sequencialmente. Isto reduz a tensão muscular que pode desencadear respostas inflamatórias e apoia o tónus vagal."
    },
    13: {
      title: "Alimentos com Zinco e Vitamina C",
      description: "Inclui alimentos com zinco (sementes de abóbora, leguminosas) e vitamina C (citrinos, pimentos) se aprovado pela equipa médica. Estes nutrientes apoiam várias funções das células imunológicas."
    },
    14: {
      title: "Meditação de Varrimento Corporal",
      description: "Pratica um varrimento corporal de 5 minutos, trazendo consciência a cada parte do corpo sequencialmente. Isto reduz os hormonas de stress que podem suprimir a função imunológica."
    },
    15: {
      title: "Alimentos Prebióticos",
      description: "Incorpora alimentos prebióticos como bananas, aveia e maçãs, se tolerado. Estes apoiam bactérias intestinais que produzem ácidos gordos de cadeia curta, que regulam as respostas imunológicas."
    },
    16: {
      title: "Duche com Contraste de Temperatura",
      description: "Tenta terminar o duche com 30 segundos de água mais fresca (não fria). Este stress ligeiro pode estimular a circulação das células imunológicas e promover resiliência."
    },
    17: {
      title: "Terapia do Riso",
      description: "Vê ou lê algo que te faça rir. O riso genuíno aumenta a atividade das células matadoras naturais e diminui as hormonas de stress que podem suprimir a imunidade."
    },
    18: {
      title: "Coordenação Olho-Mão",
      description: "Pratica atividades simples que exigem coordenação olho-mão como malabarismo suave ou desenho. Estas atividades estimulam vias neurais que influenciam a regulação imunológica."
    },
    19: {
      title: "Posturas Restaurativas de Ioga",
      description: "Experimenta posturas suaves de ioga restaurativo como pernas na parede ou posição da criança. Estas ativam o sistema nervoso parassimpático, apoiando a função imunológica."
    },
    20: {
      title: "Desconexão Digital Noturna",
      description: "Define uma hora para desligar os ecrãs, parando de os usar 1-2 horas antes de dormir. A exposição à luz azul pode prejudicar a melatonina, que tem funções imunomoduladoras."
    },
    21: {
      title: "Técnica de Respiração 4-7-8",
      description: "Experimenta a técnica de respiração 4-7-8: inspira durante 4 contagens, prende durante 7, expira durante 8. Este padrão reduz citocinas inflamatórias e melhora a qualidade do sono."
    },
    22: {
      title: "Contacto com a Natureza",
      description: "Passa 20 minutos num ambiente natural, se possível. A investigação mostra que banhos de floresta aumentam a atividade das células matadoras naturais por até 7 dias depois."
    },
    23: {
      title: "Estimulação do Nervo Vago",
      description: "Massaja suavemente a área atrás dos lóbulos das orelhas durante 1-2 minutos. Isto estimula o nervo vago, reduzindo a inflamação e apoiando a regulação imunológica."
    },
    24: {
      title: "Escrita Expressiva",
      description: "Escreve sobre os teus sentimentos durante 15-20 minutos. Esta prática reduz as hormonas de stress e marcadores inflamatórios enquanto melhora a função das células T."
    },
    25: {
      title: "Alimentos Ricos em Magnésio",
      description: "Inclui fontes de magnésio como vegetais de folha verde escura e frutos secos, se tolerado. O magnésio ajuda a regular a resposta do eixo hipotálamo-hipófise-suprarrenal ao stress, reduzindo a produção excessiva de cortisol."
    },
    26: {
      title: "Torções Suaves de Ioga",
      description: "Pratica torções espinhais suaves sentado, se apropriado para a tua condição. Estes movimentos estimulam a atividade do sistema nervoso parassimpático e apoiam a circulação linfática."
    },
    27: {
      title: "Ancoragem pelos Sentidos",
      description: "Quando sentes ansiedade, pratica a técnica 5-4-3-2-1: nota 5 coisas que vês, 4 que sentes, 3 que ouves, 2 que cheiras, 1 que provas. Isto interrompe cascatas de stress que suprimem a imunidade."
    },
    28: {
      title: "Prática de Zumbido Vocal",
      description: "Zumbe uma melodia simples durante 2-3 minutos. Esta vibração aumenta o óxido nítrico nas passagens nasais, que tem propriedades antimicrobianas e apoia a função imunológica."
    },
    29: {
      title: "Especiarias Anti-Inflamatórias",
      description: "Adiciona açafrão-da-índia e gengibre às refeições, se aprovado pela equipa de saúde. Estas especiarias contêm compostos que modulam vias inflamatórias e apoiam o equilíbrio imunológico."
    },
    30: {
      title: "Ambiente Ideal para Dormir",
      description: "Mantém o quarto fresco (18-20°C) e completamente escuro. Estas condições otimizam a produção de melatonina, que tem propriedades promotoras de sono e imunomoduladoras."
    },
    31: {
      title: "Recordação de Memórias Positivas",
      description: "Passa 3-5 minutos a recordar uma memória feliz em detalhe. Esta prática ativa regiões do cérebro associadas a emoções positivas e reduz biomarcadores inflamatórios."
    },
    32: {
      title: "Percussão Linfática Suave",
      description: "Bate levemente no corpo com as pontas dos dedos, começando pelas extremidades e movendo em direção ao coração. Isto estimula o fluxo linfático, ajudando a circulação das células imunológicas."
    },
    33: {
      title: "Respiração Centrada no Coração",
      description: "Coloca a mão sobre o coração e respira lentamente enquanto te focas em sentimentos de gratidão. Isto muda os padrões da variabilidade da frequência cardíaca, apoiando a função imunológica."
    },
    34: {
      title: "Alimentos Fermentados",
      description: "Inclui pequenas quantidades de alimentos fermentados como iogurte, se tolerado. Estes apoiam a diversidade do microbioma intestinal, que desempenha um papel crucial na educação do sistema imunológico."
    },
    35: {
      title: "Relaxamento Guiado ao Deitar",
      description: "Ouve uma sessão de relaxamento corporal guiado de 10 minutos antes de dormir. Isto reduz os níveis noturnos de cortisol e apoia a função saudável das células imunológicas durante as horas críticas da noite."
    },
    36: {
      title: "Expressão Criativa",
      description: "Pratica atividades criativas simples e agradáveis como colorir ou modelagem com barro. A expressão criativa está ligada a melhor atividade das células matadoras naturais e inflamação reduzida."
    },
    37: {
      title: "Banhos de Sol Matinais",
      description: "Recebe 10-15 minutos de sol matinal, se possível. Isto ajuda a regular o ritmo circadiano e apoia a produção de vitamina D, que tem efeitos imunomoduladores importantes."
    },
    38: {
      title: "Pausas para Movimentar",
      description: "Levanta-te e move-te suavemente durante 3-5 minutos a cada hora, se conseguires. Isto melhora a circulação e o fluxo linfático, reduzindo marcadores inflamatórios associados ao tempo prolongado sentado."
    },
    39: {
      title: "Visualização do Sistema Imunitário",
      description: "Visualiza as células imunológicas a funcionarem de forma ótima durante 5 minutos. A visualização guiada aumenta a imunoglobulina A salivar e melhora a atividade das células matadoras naturais."
    },
    40: {
      title: "Prática de Autocompaixão",
      description: "Quando enfrentas dificuldades, fala contigo com gentileza como farias com um amigo querido. A autocompaixão reduz respostas inflamatórias ao stress e apoia a resiliência imunológica."
    }
  }
};

// Daily Tips category translations
export const dailyTipsCategoryTranslations = {
  en: {
    immune: "Immune System",
    stress: "Stress Management",
    physical: "Physical Activity",
    mental: "Mental Health",
    sleep: "Sleep",
    nutrition: "Nutrition"
  },
  pt: {
    immune: "Sistema Imunitário",
    stress: "Gestão de Stress",
    physical: "Atividade Física",
    mental: "Saúde Mental",
    sleep: "Sono",
    nutrition: "Nutrição"
  }
} as const;

// Meditation translations (European Portuguese - informal tu form)
export const meditationsTranslations = {
  pt: {
    1: { 
      title: "Tom de Cura de 432 Hz", 
      description: "Ouve a frequência de 432 Hz, que se acredita ressoar com as frequências naturais do corpo e apoiar a função imunológica." 
    },
    2: { 
      title: "Batidas Binaurais Theta de 6 Hz", 
      description: "Experimenta batidas binaurais de ondas theta (6 Hz) que podem ajudar a induzir um estado profundamente relaxado e meditativo que apoia a cura." 
    },
    3: { 
      title: "Frequência de Reparação de 528 Hz", 
      description: "Frequentemente chamada o 'Tom Milagroso', a frequência de 528 Hz está associada à reparação de ADN e transformação nas tradições de terapia sonora." 
    },
    4: { 
      title: "Tons Delta de Sono Profundo 4 Hz", 
      description: "Batidas binaurais de ondas delta (4 Hz) podem ajudar a encorajar estados de sono profundo e apoiar os ciclos de descanso do corpo." 
    },
    5: { 
      title: "Frequência Energética de 417 Hz", 
      description: "O tom de 417 Hz está associado a facilitar a mudança e remover energia negativa nas práticas de cura através do som." 
    },
    6: { 
      title: "Ruído Branco para Foco Mental", 
      description: "O ruído branco consistente pode ajudar a criar uma paisagem sonora que mascara distrações e apoia a prática de atenção plena." 
    },
    7: { 
      title: "Tom de Conexão de 639 Hz", 
      description: "Acredita-se que a frequência de 639 Hz encoraja relações harmoniosas e comunicação celular no corpo." 
    },
    8: { 
      title: "Terapia de Ondas Alfa 8 Hz", 
      description: "Batidas binaurais de ondas alfa (8 Hz) podem ajudar a reduzir a ansiedade e promover um estado de alerta calmo e relaxado antes dos tratamentos." 
    },
    9: { 
      title: "Frequência de Libertação de 396 Hz", 
      description: "Na terapia sonora, 396 Hz está associada a libertar padrões de medo e bloqueios negativos que podem impedir a cura." 
    },
    10: { 
      title: "Ruído Branco de Ritmo Constante", 
      description: "Este ruído branco suave pode apoiar exercícios de respiração e fornecer uma base sonora constante para práticas de apoio imunológico." 
    }
  }
};

// Meditation category translations
export const meditationCategoryTranslations = {
  en: {
    immune: "Immune Support",
    relaxation: "Relaxation",
    healing: "Healing",
    mindfulness: "Mindfulness",
    sleep: "Sleep",
    energy: "Energy"
  },
  pt: {
    immune: "Apoio Imunitário",
    relaxation: "Relaxamento",
    healing: "Cura",
    mindfulness: "Atenção Plena",
    sleep: "Sono",
    energy: "Energia"
  }
} as const;

// Mood intensity labels (for the 1-5 slider)
export const moodLabelTranslations = {
  en: {
    1: 'Very low',
    2: 'Low',
    3: 'Neutral',
    4: 'Good',
    5: 'Excellent'
  },
  pt: {
    1: 'Muito baixo',
    2: 'Baixo',
    3: 'Neutro',
    4: 'Bom',
    5: 'Excelente'
  }
} as const;

// Fatigue intensity labels (for the 1-5 slider)
export const fatigueLabelTranslations = {
  en: {
    1: 'Exhausted',
    2: 'Very tired',
    3: 'Moderate',
    4: 'Energetic',
    5: 'Very energetic'
  },
  pt: {
    1: 'Exausto',
    2: 'Muito cansado',
    3: 'Moderado',
    4: 'Energético',
    5: 'Muito energético'
  }
} as const;
